import { AsyncSelectInput } from '@/components/Forms/AsyncSelect';
import FormSection from '@/components/Forms/FormSection';
import { debounceAsync } from '@/helpers/debounceAsync';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { useSearchProductsMutation } from '@/services/productCatalog';
import {
  Box,
  Button,
  FormikSelectInput,
  FormikTextInput,
  SimulatedEventPayloadType,
  toast,
  useBreakpoint,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  HVACEquipmentItem,
  HVACEquipmentSizeEfficiencyMap,
  HVACEquipmentType,
  HVACEquipmentTypeLabels,
  UserPermissions,
} from 'types';

interface EquipmentAndMaterialsSectionProps {
  systemIndex: number;
  isPreliminary?: boolean;
  helpLink?: string;
  isPackageEditable?: boolean;
  isFinCoUser?: boolean;
}

const dataKey = 'equipment' as const;
const label = 'Equipment & Materials' as const;

export const EquipmentAndMaterialsSection = ({
  systemIndex,
  isPreliminary = false,
  helpLink,
  isPackageEditable = true,
  isFinCoUser = false,
}: EquipmentAndMaterialsSectionProps) => {
  const { isTablet, isPhone } = useBreakpoint();
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue, handleBlur } = useFormikContext<any>();

  const equipmentTypes = useMemo(() => {
    const equipmentMaterials = Object.values(HVACEquipmentType);
    return equipmentMaterials
      .map((type, index) => ({
        label: HVACEquipmentTypeLabels[type],
        value: type,
        index,
      }))
      .toSorted((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }, []);

  const [triggerSearch] = useSearchProductsMutation();
  const handleSearchText = async (searchText: string, productType: string) => {
    const { data, error } = await triggerSearch({
      ...(searchText ? { query: searchText } : {}),
      limit: 20,
      page: 1,
      filters: [
        {
          filter: 'attribute',
          key: 'lightreach-product-type',
          operation: {
            equals: productType,
          },
        },
        {
          filter: 'availableOnDate',
          operation: {
            equals: new Date().toISOString(),
          },
        },
      ],
    });
    if (error) {
      toast.error('Error searching products. Please try again.');
      return undefined;
    }
    const productData = data?.data?.map((product) => {
      const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
      const productType = product.attributes.find((attr) => attr.key === 'lightreach-product-type')?.value ?? '';
      const isSerialNumberRequired =
        product.attributes.find((attr) => attr.key === 'lightreach-serial-number-required')?.value ?? false;
      return {
        label: `${product.manufacturer} - ${model}`,
        value: product.id,
        manufacturer: product.manufacturer,
        model,
        productType,
        name: product.name,
        isSerialNumberRequired,
      };
    });
    return productData?.length && (data?.paging?.totalPages || 0) > 1
      ? [
          {
            label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
            value: undefined,
          },
          ...productData,
        ]
      : productData;
  };

  const debouncedSearch = debounceAsync(
    (value: string, equipmentType: HVACEquipmentType) => handleSearchText(value, equipmentType),
    400,
    2,
    true,
  );

  return (
    <FormSection
      title={label}
      description={
        <Box gap="sm" fontSize="xs">
          <Box>Update applicable {label} for the proposal.</Box>
          {helpLink && (
            <Box display="block">
              For more information about System Categories, check the{' '}
              <Link to={helpLink} target="_blank" rel="noopener noreferrer">
                Knowledge Base
              </Link>
            </Box>
          )}
        </Box>
      }
      baseStyle={{
        borderWidth: '0',
        'data-intercom-target': intercomTarget('system-design', label),
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}[${dataKey}].items`}
        render={(arrayHelpers) => {
          return (
            <Box gap="lg">
              <Box
                direction={isPreliminary ? { base: 'column', tablet: 'row' } : { base: 'column', hd: 'row' }}
                gap="md"
              >
                <Box maxWidth="3xl" width="100%">
                  <Field
                    label={`Add ${label}`}
                    name={`systems.${systemIndex}[${dataKey}].equipmentType`}
                    id={`systems.${systemIndex}[${dataKey}].equipmentType`}
                    placeholder={`Select new ${dataKey} type...`}
                    component={FormikSelectInput}
                    options={equipmentTypes}
                    isDisabled={
                      userOnlyHasReaderPermission ||
                      !values?.systems[systemIndex]?.systemCategory ||
                      !isPackageEditable
                    }
                  />
                </Box>
                {isPreliminary ? (
                  <Button
                    as="button"
                    variant="primary"
                    tone="primary"
                    isDisabled={
                      userOnlyHasReaderPermission ||
                      (isPreliminary
                        ? dataKey === 'equipment'
                          ? !values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value
                          : !values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value
                        : false) ||
                      !isPackageEditable
                    }
                    iconPrefix="add"
                    size="md"
                    style={{
                      alignSelf: isTablet || isPhone ? 'start' : 'end',
                    }}
                    onClick={() => {
                      arrayHelpers.push({
                        type: values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value,
                        size: '',
                        efficiencies: [],
                        quantity: 1,
                      });
                      void setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                    }}
                  >
                    Add
                  </Button>
                ) : (
                  <AsyncSelectInput
                    id={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                    name={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                    label={`Search ${label}`}
                    loadOnOpen={!!values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value}
                    loadOptions={async (input: string) => {
                      const options = await debouncedSearch(
                        input,
                        values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value,
                      );
                      return [
                        ...options,
                        {
                          label: (
                            <Button
                              as="button"
                              variant="secondary"
                              size="sm"
                              isDisabled={!isPackageEditable}
                              onClick={() => {
                                arrayHelpers.push({
                                  type: values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value,
                                  manufacturer: '',
                                  model: '',
                                  quantity: 1,
                                  productId: 'other',
                                  name: '',
                                  isOther: true,
                                });
                                void setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                              }}
                            >
                              Enter {values?.systems[systemIndex]?.[dataKey]?.equipmentType.label} not found in
                              search
                            </Button>
                          ),
                          value: 'other',
                        },
                      ];
                    }}
                    isDisabled={
                      userOnlyHasReaderPermission ||
                      !values.systems?.[systemIndex]?.[dataKey]?.equipmentType ||
                      !isPackageEditable
                    }
                    onChange={(event) => {
                      if (event?.target?.value && event.target.value.value && event.target.value.value !== 'other') {
                        arrayHelpers.push({
                          type: values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value,
                          manufacturer: event?.target?.value.manufacturer,
                          model: event?.target?.value.model,
                          quantity: 1,
                          productId: event?.target?.value.value,
                          name: event?.target?.value.name,
                          isOther: false,
                        });
                        void setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                      }
                    }}
                    onBlur={handleBlur}
                    isClearable
                    value={''}
                    placeholder="Search by Model or Name..."
                    noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) => {
                      const equipmentLabel = values?.systems[systemIndex]?.[dataKey]?.equipmentType?.label;

                      if (!inputValue) return `Type to search for ${equipmentLabel}...`;
                      return (
                        <Box display="flex" direction="column" gap="sm">
                          <div>No {equipmentLabel} found</div>
                          <Button
                            as="button"
                            variant="secondary"
                            size="sm"
                            isDisabled={!isPackageEditable}
                            onClick={() => {
                              arrayHelpers.push({
                                type: values?.systems[systemIndex]?.[dataKey]?.equipmentType?.value,
                                manufacturer: '',
                                model: '',
                                quantity: 1,
                                productId: 'other',
                                name: '',
                                isOther: true,
                              });
                              void setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                            }}
                          >
                            Enter {equipmentLabel} not found in search
                          </Button>
                        </Box>
                      );
                    }}
                  />
                )}
              </Box>
              {isPreliminary
                ? values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
                    (equipmentMaterial: HVACEquipmentItem & { isRequiredEquipment?: boolean }, index: number) => {
                      const equipmentSize =
                        dataKey === 'equipment' && equipmentMaterial.type
                          ? HVACEquipmentSizeEfficiencyMap[equipmentMaterial.type]?.size
                          : undefined;
                      const equipmentEfficiencies =
                        dataKey === 'equipment' && equipmentMaterial.type
                          ? (HVACEquipmentSizeEfficiencyMap[equipmentMaterial.type]?.efficiencies ?? [])
                          : [];
                      return (
                        <Box
                          key={`system-${systemIndex}-${dataKey}-${equipmentMaterial.type}-${index}`}
                          direction="row"
                          gap="lg"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box direction="column" gap="sm" width={{ base: '60', tablet: '40' }} alignSelf="center">
                            <Box fontSize="md" fontWeight="medium">
                              {HVACEquipmentTypeLabels[equipmentMaterial.type]}
                            </Box>
                            <Box gap="md">
                              {equipmentSize && Object.keys(equipmentSize).length ? (
                                <Field
                                  label={`Size (${equipmentSize.unit})`}
                                  name={`systems.${systemIndex}[${dataKey}].items.${index}.size`}
                                  id={`systems.${systemIndex}[${dataKey}].items.${index}.size`}
                                  component={FormikSelectInput}
                                  options={equipmentSize.values.map((size) => ({
                                    value: size,
                                    label: size,
                                    unit: equipmentSize.unit,
                                  }))}
                                  isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                />
                              ) : null}
                              {equipmentEfficiencies?.length ? (
                                <FieldArray
                                  name={`systems.${systemIndex}[${dataKey}].items.${index}.efficiencies`}
                                  render={() => {
                                    return (
                                      <Box direction={{ base: 'column', hd: 'row' }} gap="lg">
                                        {equipmentEfficiencies?.map(
                                          ({ unit, values: efficiencyValues }, efficiencyIndex) => {
                                            return (
                                              <Field
                                                key={`system-${systemIndex}-${dataKey}-efficiency-${efficiencyIndex}`}
                                                label={`Efficiency (${unit})`}
                                                name={`systems.${systemIndex}[${dataKey}].items.${index}.efficiencies.${efficiencyIndex}`}
                                                id={`systems.${systemIndex}[${dataKey}].items.${index}.efficiencies.${efficiencyIndex}`}
                                                component={FormikSelectInput}
                                                options={efficiencyValues.map((size) => ({
                                                  value: size,
                                                  label: size,
                                                  unit,
                                                }))}
                                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                              />
                                            );
                                          },
                                        )}
                                      </Box>
                                    );
                                  }}
                                />
                              ) : null}
                            </Box>
                            {equipmentMaterial?.manufacturer || equipmentMaterial?.model ? (
                              <Box gap="xs" direction="column" fontSize="sm">
                                <Box fontSize="sm">
                                  {equipmentMaterial.manufacturer} - {equipmentMaterial.model}
                                </Box>
                                {equipmentMaterial.name && equipmentMaterial.productId !== 'other' && (
                                  <Box fontSize="sm" style={{ fontStyle: 'italic' }}>
                                    ({equipmentMaterial.name})
                                  </Box>
                                )}
                              </Box>
                            ) : null}
                          </Box>
                          <Box direction="column" gap="sm" flex={{ tablet: 'auto' }}>
                            <Box
                              direction={{ base: 'column', tablet: 'row' }}
                              gap="sm"
                              justifyContent="space-between"
                            >
                              <Field
                                label={'Quantity'}
                                type="number"
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                component={FormikTextInput}
                                maxWidth="lg"
                                width="100"
                                min={1}
                                max={10}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                onChange={(event: SimulatedEventPayloadType) => {
                                  let value =
                                    event?.target?.value?.trim() === '' ? 1 : parseInt(event?.target?.value, 10);
                                  if (isNaN(value) || value < 1) {
                                    value = 1;
                                  }

                                  if (
                                    value > 1 &&
                                    equipmentMaterial.type !== HVACEquipmentType.ductlessIndoor &&
                                    equipmentMaterial.type !== HVACEquipmentType.ductlessOutdoor
                                  ) {
                                    if (
                                      window.confirm(
                                        'Are you sure this shouldn’t be a separate System?\n\nIt looks like you’re trying to add multiple pieces of the same type of equipment. As a reminder, each system requires an AHRI matchup, and failing to properly input each system will result in a rejection at Install Review.',
                                      )
                                    ) {
                                      void setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                        value,
                                      );
                                    }
                                  } else {
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                      value,
                                    );
                                  }
                                }}
                                onBlur={(event: SimulatedEventPayloadType) => {
                                  if (event?.target?.value?.trim() === '') {
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                      1,
                                    );
                                  }
                                }}
                              />
                              <Box padding="lg 0 0 0">
                                {(!equipmentMaterial?.isRequiredEquipment || isFinCoUser) && (
                                  <Button
                                    as="button"
                                    variant="tertiary"
                                    tone="danger"
                                    isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                    onClick={() => {
                                      if (
                                        arrayHelpers.form.values.systems[systemIndex][dataKey].items?.length === 1
                                      ) {
                                        //If we remove the last item, we set up the totalCost to 0
                                        void setFieldValue(`systems.${systemIndex}[${dataKey}].totalCost`, 0);
                                      }
                                      arrayHelpers.remove(index);
                                    }}
                                    style={{ padding: '0' }}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    },
                  )
                : values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
                    (
                      equipmentMaterial: HVACEquipmentItem & {
                        isSerialNumberRequired?: boolean;
                        isOther?: boolean;
                      },
                      index: number,
                    ) => {
                      return (
                        <Box
                          key={`system-${systemIndex}-${dataKey}-${equipmentMaterial.type}-${index}`}
                          direction="row"
                          gap="lg"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box direction="column" gap="sm" width={{ base: '60', tablet: '40' }} alignSelf="center">
                            <Box fontSize="md" fontWeight="medium">
                              {HVACEquipmentTypeLabels[equipmentMaterial.type]}
                            </Box>
                            {equipmentMaterial.isOther ? (
                              <Box childGap="sm">
                                <Field
                                  label={'Manufacturer'}
                                  name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                  id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                  component={FormikTextInput}
                                  isDisabled={!isPackageEditable}
                                  isRequired
                                />
                                <Field
                                  label={'Model'}
                                  name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                  id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                  component={FormikTextInput}
                                  isDisabled={!isPackageEditable}
                                  isRequired
                                />
                              </Box>
                            ) : null}
                            {equipmentMaterial.manufacturer &&
                            equipmentMaterial.model &&
                            !equipmentMaterial.isOther ? (
                              <Box fontSize="sm">
                                {equipmentMaterial.manufacturer} - {equipmentMaterial.model}{' '}
                                {equipmentMaterial.productId === 'other' ? '*' : ''}
                              </Box>
                            ) : null}
                            {!(equipmentMaterial.manufacturer && equipmentMaterial.manufacturer !== '') &&
                            !(equipmentMaterial.model && equipmentMaterial.model !== '') &&
                            !equipmentMaterial.isOther ? (
                              <AsyncSelectInput
                                id={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                                name={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                                label={`Search ${label}`}
                                loadOnOpen={!!values?.systems[systemIndex]?.[dataKey]?.items?.[index]?.type}
                                loadOptions={async (input: string) => {
                                  const options = await debouncedSearch(
                                    input,
                                    values?.systems[systemIndex]?.[dataKey]?.items?.[index]?.type,
                                  );
                                  return [
                                    ...options,
                                    {
                                      label: (
                                        <Button
                                          as="button"
                                          variant="secondary"
                                          size="sm"
                                          onClick={() => {
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                              '',
                                            );
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                              '',
                                            );
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                              1,
                                            );
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                              'other',
                                            );
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                              '',
                                            );
                                            void setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                              true,
                                            );
                                          }}
                                        >
                                          Enter item not found in search
                                        </Button>
                                      ),
                                      value: 'other',
                                    },
                                  ];
                                }}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                onChange={(event) => {
                                  if (
                                    event?.target?.value &&
                                    event.target.value.value &&
                                    event.target.value.value !== 'other'
                                  ) {
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                      event?.target?.value.manufacturer,
                                    );
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                      event?.target?.value.model,
                                    );
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                      1,
                                    );
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                      event?.target?.value.value,
                                    );
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                      event?.target?.value.name,
                                    );
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                      false,
                                    );
                                  }
                                }}
                                onBlur={handleBlur}
                                isClearable
                                value={''}
                                placeholder="Search by Model or Name..."
                                noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) => {
                                  if (!inputValue) return `Type to search...`;
                                  return (
                                    <Box display="flex" direction="column" gap="sm">
                                      <div>Not found</div>
                                      <Button
                                        as="button"
                                        variant="secondary"
                                        size="sm"
                                        isDisabled={!isPackageEditable}
                                        onClick={() => {
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                            '',
                                          );
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                            '',
                                          );
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                            1,
                                          );
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                            'other',
                                          );
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                            '',
                                          );
                                          void setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                            true,
                                          );
                                        }}
                                      >
                                        Enter item not found in search
                                      </Button>
                                    </Box>
                                  );
                                }}
                              />
                            ) : null}
                            {equipmentMaterial.name &&
                            !(equipmentMaterial.isOther || equipmentMaterial.productId === 'other') ? (
                              <Box fontSize="sm" style={{ fontStyle: 'italic' }}>
                                ({equipmentMaterial.name})
                              </Box>
                            ) : null}
                          </Box>
                          <Box direction="column" gap="sm" flex={{ tablet: 'auto' }}>
                            <Box
                              direction={{ base: 'column', tablet: 'row' }}
                              gap="sm"
                              justifyContent="space-between"
                            >
                              <Field
                                label={'Quantity'}
                                type="number"
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                component={FormikTextInput}
                                maxWidth="lg"
                                width="100"
                                min={1}
                                max={10}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                onChange={(event: SimulatedEventPayloadType) => {
                                  let value =
                                    event?.target?.value?.trim() === '' ? 1 : parseInt(event?.target?.value, 10);
                                  if (isNaN(value) || value < 1) {
                                    value = 1;
                                  }
                                  void setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                    value,
                                  );
                                }}
                                onBlur={(event: SimulatedEventPayloadType) => {
                                  if (event?.target?.value?.trim() === '') {
                                    void setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                      1,
                                    );
                                  }
                                }}
                              />
                              <Box padding="lg 0 0 0">
                                {(!values.systems[systemIndex][dataKey].items[index].isRequiredEquipment ||
                                  isFinCoUser) && (
                                  <Button
                                    as="button"
                                    variant="tertiary"
                                    tone="danger"
                                    isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                    onClick={() => {
                                      if (
                                        arrayHelpers.form.values.systems[systemIndex][dataKey].items?.length === 1
                                      ) {
                                        //If we remove the last item, we set up the totalCost to 0
                                        void setFieldValue(`systems.${systemIndex}[${dataKey}].totalCost`, 0);
                                      }
                                      arrayHelpers.remove(index);
                                    }}
                                    style={{ padding: '0' }}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    },
                  )}
            </Box>
          );
        }}
      />
    </FormSection>
  );
};
